import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Tooltip,
  Button,
  Stack
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { useMemo, useCallback } from 'react';
import { DeliveryStatus } from '../constants/statuses';
import { toggleInventoryOrderDeliveryStatus } from '../utils/api';

const InventoryOrdersTable = ({
  orders,
  onEdit,
  onDelete,
  isRMVendor,
  onSuccess
}) => {
  const DELIVERY_STATUS_OPTIONS = [
    {
      label: 'Pending',
      value: DeliveryStatus.PENDING,
      color: '#ff9800',
      bgColor: '#fff8e1',
      icon: 'clock'
    },
    {
      label: 'Shipped',
      value: DeliveryStatus.SHIPPED,
      color: '#7b1fa2',
      bgColor: '#f3e5f5',
      icon: 'send'
    },
    {
      label: 'Partially Delivered',
      value: DeliveryStatus.PARTIALLY_DELIVERED,
      color: '#2196f3',
      bgColor: '#e3f2fd',
      icon: 'truck'
    },
    {
      label: 'Delivered',
      value: DeliveryStatus.DELIVERED,
      color: '#4caf50',
      bgColor: '#e8f5e9',
      icon: 'check-circle'
    },
    {
      label: 'Cancelled',
      value: DeliveryStatus.CANCELLED,
      color: '#f44336',
      bgColor: '#ffebee',
      icon: 'x-circle'
    },
  ];

  const deliveryStatusOptions = useMemo(() =>
    DELIVERY_STATUS_OPTIONS.map(option => (
      <MenuItem
        key={option.value}
        value={option.value}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '&:hover': {
            backgroundColor: option.bgColor,
          },
          '&.Mui-selected': {
            backgroundColor: option.bgColor,
            color: option.color,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: option.bgColor,
            }
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
          <FeatherIcon
            icon={option.icon}
            size={16}
            color={option.color}
          />
        </ListItemIcon>
        <ListItemText
          primary={option.label}
          sx={{
            '& .MuiListItemText-primary': {
              fontSize: '0.875rem',
              fontWeight: 500
            }
          }}
        />
      </MenuItem>
    )),
    []
  );

  const handleDeliveryStatusChange = useCallback(async (orderId, newStatus) => {
    try {
      console.log('handleDeliveryStatusChange called with orderId:', orderId, 'newStatus:', newStatus);

      const statusOption = DELIVERY_STATUS_OPTIONS.find(opt => opt.value === newStatus);
      if (!statusOption) {
        console.error('Invalid status option:', newStatus);
        return;
      }

      console.log('Showing Swal confirmation for status:', statusOption.label);

      const result = await Swal.fire({
        title: 'Update Delivery Status',
        text: `Are you sure you want to change the delivery status to ${statusOption?.label}?`,
        input: 'textarea',
        inputPlaceholder: 'Enter delivery notes (optional)',
        inputAttributes: {
          maxlength: 1000,
        },
        showCancelButton: true,
        confirmButtonText: 'Yes, update it!',
        cancelButtonText: 'Cancel',
        confirmButtonColor: statusOption?.color || '#1976d2',
        icon: 'warning',
      });

      console.log('Swal result:', result);

      if (result.isConfirmed) {
        Swal.fire({
          title: 'Updating...',
          text: 'Please wait while we update the delivery status',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          },
        });

        console.log('Preparing payload:', { deliveryStatus: newStatus, deliveryComments: result.value.trim() || undefined });

        const payload = {
          deliveryStatus: newStatus,
          deliveryComments: result.value.trim() || undefined,
        };

        console.log('Calling toggleInventoryOrderDeliveryStatus with orderId:', orderId, 'payload:', payload);
        const response = await toggleInventoryOrderDeliveryStatus(orderId, payload);
        console.log('API response:', response);

        Swal.fire({
          title: 'Success!',
          text: 'Delivery status updated successfully',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });

        console.log('onSuccess function:', onSuccess);

        if (onSuccess && typeof onSuccess === 'function') {
          console.log('Executing onSuccess callback');
          await onSuccess();
        }
      }
    } catch (error) {
      console.error('Error updating delivery status:', error);
      Swal.fire({
        title: 'Error!',
        text: error?.message || 'Failed to update delivery status',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }, [onSuccess]);

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `$${num.toFixed(2)}`;
  };

  const columns = [
    {
      dataField: 'orderNumber',
      text: 'Order #',
      sort: true,
      headerStyle: { width: '120px' },
      formatter: (cell, row) => row.reference_id || `ORD${cell}`,
    },
    {
      dataField: 'vendorId',
      text: 'Supplier',
      sort: true,
      headerStyle: { width: '200px' },
      formatter: (cell, row) => {
        if (row.vendorId?.companyName) {
          return row.vendorId.companyName;
        }
        if (row.supplier_id?.first_name && row.supplier_id?.last_name) {
          return `${row.supplier_id.first_name} ${row.supplier_id.last_name}`;
        }
        return 'N/A';
      },
    },
    {
      dataField: 'createdAt',
      text: 'Order Date',
      sort: true,
      headerStyle: { width: '150px' },
      formatter: (cell, row) => {
        const date = cell || row.order_date;
        return date ? new Date(date).toLocaleDateString() : 'N/A';
      },
    },
    {
      dataField: 'orderedItems',
      text: 'Items',
      headerStyle: { width: '100px' },
      formatter: (cell, row) => {
        const items = cell || row.ordered_items || [];
        return Array.isArray(items) ? items.length : 0;
      },
    },
    ...(!isRMVendor ? [{
      dataField: 'status',
      text: 'Order Status',
      sort: true,
      headerStyle: { width: '120px' },
      formatter: (cell, row) => {
        let status = cell || 'pending';
        const getStatusStyle = (status) => {
          if (!status) return { backgroundColor: '#f5f5f5', color: '#757575' };
          switch (status.toString().toLowerCase()) {
            case 'draft':
              return { backgroundColor: '#e3f2fd', color: '#1976d2' };
            case 'submitted':
              return { backgroundColor: '#fff8e1', color: '#ff9800' };
            case 'approved':
              return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
            case 'rejected':
              return { backgroundColor: '#ffebee', color: '#d32f2f' };
            case 'completed':
              return { backgroundColor: '#e0f2f1', color: '#00796b' };
            default:
              return { backgroundColor: '#f5f5f5', color: '#757575' };
          }
        };
        return (
          <Chip
            label={status.toLowerCase()}
            size="small"
            sx={{
              ...getStatusStyle(status),
              textTransform: 'capitalize',
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />
        );
      },
    }] : []),
    {
      dataField: 'delivery_status_select',
      text: 'Delivery Status',
      sort: true,
      headerStyle: { width: '140px' },
      formatter: (cell, row) => {
        let currentStatus = row.delivery?.status || row.delivery_status || DeliveryStatus.PENDING;
        const statusLower = currentStatus.toString().toLowerCase().trim();
        const statusMapping = {
          'pending': DeliveryStatus.PENDING,
          'shipped': DeliveryStatus.SHIPPED,
          'ship': DeliveryStatus.SHIPPED,
          'partial': DeliveryStatus.PARTIALLY_DELIVERED,
          'partially delivered': DeliveryStatus.PARTIALLY_DELIVERED,
          'partially_delivered': DeliveryStatus.PARTIALLY_DELIVERED,
          'delivered': DeliveryStatus.DELIVERED,
          'cancelled': DeliveryStatus.CANCELLED,
          'canceled': DeliveryStatus.CANCELLED,
          '': DeliveryStatus.PENDING,
          'null': DeliveryStatus.PENDING,
          'undefined': DeliveryStatus.PENDING,
        };

        const normalizedStatus = statusMapping[statusLower] || DeliveryStatus.PENDING;
        const currentOption = DELIVERY_STATUS_OPTIONS.find(opt => opt.value === normalizedStatus);

        return (
          <Select
            value={normalizedStatus}
            onChange={(e) => handleDeliveryStatusChange(row._id, e.target.value)}
            size="small"
            disabled={normalizedStatus === DeliveryStatus.DELIVERED}
            sx={{
              minWidth: 120,
              '.MuiSelect-select': {
                py: 0.5,
                px: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: currentOption?.bgColor || '#f5f5f5',
                color: currentOption?.color || '#757575',
                border: `1px solid ${currentOption?.color || '#e0e0e0'}`,
                borderRadius: 1,
              },
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: `2px solid ${currentOption?.color || '#1976d2'}`
              },
              '& .MuiSelect-icon': {
                color: currentOption?.color || '#757575'
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  '& .MuiList-root': {
                    py: 1
                  }
                }
              }
            }}
            renderValue={(value) => {
              const option = DELIVERY_STATUS_OPTIONS.find(opt => opt.value === value);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FeatherIcon
                    icon={option?.icon || 'help-circle'}
                    size={14}
                    color={option?.color || '#757575'}
                  />
                  {option?.label || 'Unknown Status'}
                </Box>
              );
            }}
          >
            {deliveryStatusOptions}
          </Select>
        );
      },
    },
    {
      dataField: 'orderDetails',
      text: 'Ordered By',
      sort: true,
      headerStyle: { width: '150px' },
      formatter: (cell, row) => {
        if (row.ordered_by?.first_name) {
          const firstName = row.ordered_by.first_name.charAt(0).toUpperCase() +
            row.ordered_by.first_name.slice(1).toLowerCase();
          const lastName = row.ordered_by.last_name ?
            row.ordered_by.last_name.charAt(0).toUpperCase() +
            row.ordered_by.last_name.slice(1).toLowerCase() : '';
          return `${firstName} ${lastName}`.trim();
        }
        if (cell?.ordered?.userDetails) {
          const userDetails = cell.ordered.userDetails;
          if (userDetails.first_name && userDetails.last_name) {
            return `${userDetails.first_name} ${userDetails.last_name}`;
          }
          if (userDetails.email) {
            return userDetails.email.split('@')[0];
          }
        }
        return 'N/A';
      },
    },
    ...(!isRMVendor
      ? [{
        dataField: 'actions',
        text: 'Actions',
        headerStyle: { width: '100px' },
        formatter: (cell, row) => (
          <Box display="flex" gap={1}>
            <IconButton
              onClick={() => onEdit(row)}
              title="Edit Order"
              size="small"
              sx={{ color: '#ff9800' }}
            >
              <FeatherIcon icon="edit" size={18} />
            </IconButton>
            <IconButton
              onClick={() => onDelete(row)}
              title="Delete Order"
              size="small"
              sx={{ color: '#d32f2f' }}
            >
              <FeatherIcon icon="trash-2" size={18} />
            </IconButton>
          </Box>
        ),
      }]
      : [])
  ];

  const paginationOptions = {
    sizePerPage: 10,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true,
  };

  const NoDataIndication = () => (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
      width="100%"
    >
      <Box textAlign="center" py={4}>
        <FeatherIcon icon="package" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Orders Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no inventory orders to display
        </Typography>
      </Box>
    </Box>
  );

  const expandRow = {
    renderer: (row) => {
      const totalOrderValue = row.orderedItems?.reduce(
        (total, item) => total + ((item.requestedQuantity || 0) * (item.itemCostPerUnit || item.price || 0)),
        0
      ) || 0;

      return (
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: 2,
            border: '1px solid #e1e5e9',
            boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
            margin: 1,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            },
          }}
        >
          {/* Scrollable Container for the Entire Content */}
          <Box
            sx={{
              maxHeight: '500px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {/* Enhanced Header Section */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" sx={{
                  color: '#1a202c',
                  fontWeight: 700,
                  fontSize: 18,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <Box sx={{
                    p: 0.5,
                    backgroundColor: '#667eea',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FeatherIcon icon="package" size={20} color="white" />
                  </Box>
                  Order #{row.reference_id || `ORD${row.orderNumber}`}
                </Typography>

                <Stack direction="row" spacing={0.5}>
                  <Chip
                    icon={<FeatherIcon icon="calendar" size={12} />}
                    label={row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 'N/A'}
                    variant="outlined"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                  <Chip
                    icon={<FeatherIcon icon="dollar-sign" size={12} />}
                    label={formatCurrency(totalOrderValue)}
                    variant="outlined"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: '#16a085'
                    }}
                  />
                </Stack>
              </Stack>

              {/* Enhanced Info Grid */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  {
                    label: 'Vendor',
                    value: row.vendorId?.companyName || 'N/A',
                    icon: 'truck',
                    color: '#e74c3c'
                  },
                  {
                    label: 'Building',
                    value: row.buildingName || 'N/A',
                    icon: 'home',
                    color: '#3498db'
                  },
                  {
                    label: 'Total Items',
                    value: row.orderedItems?.length || 0,
                    icon: 'layers',
                    color: '#f39c12'
                  }
                ].map((info, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card sx={{
                      p: 1,
                      background: 'rgba(255,255,255,0.95)',
                      borderRadius: 1,
                      border: `2px solid ${info.color}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${info.color}20`,
                        borderColor: `${info.color}40`
                      }
                    }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{
                          p: 0.5,
                          backgroundColor: info.color,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FeatherIcon icon={info.icon} size={16} color="white" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {info.label}
                          </Typography>
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#2d3748',
                            fontSize: '0.875rem',
                            wordBreak: 'break-word'
                          }}>
                            {info.value}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 2, opacity: 0.3 }} />

            {/* Enhanced Comments Section with Overflow Handling */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{
                color: '#2d3748',
                fontWeight: 600,
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 1,
              }}>
                <FeatherIcon icon="message-circle" size={18} />
                Comments & Notes
              </Typography>

              <Grid container spacing={2}>
                {/* Order Comments */}
                <Grid item xs={12} md={6}>
                  <Card sx={{
                    background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
                    border: '2px solid #667eea30',
                    borderRadius: 1,
                    overflow: 'hidden',
                    height: '100%',
                    minHeight: 150
                  }}>
                    <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1" sx={{
                        color: '#667eea',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1,
                      }}>
                        <FeatherIcon icon="shopping-cart" size={14} />
                        Order Comments
                      </Typography>

                      <Box sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        maxHeight: 100,
                        '&::-webkit-scrollbar': {
                          width: '5px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#667eea',
                          borderRadius: '8px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: '#5a6fd8',
                        },
                      }}>
                        {row.comments ? (
                          <Typography variant="body2" sx={{
                            color: '#4a5568',
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {row.comments}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{
                            color: '#a0aec0',
                            fontStyle: 'italic',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}>
                            <FeatherIcon icon="message-square" size={14} />
                            No order comments available
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Delivery Comments */}
                <Grid item xs={12} md={6}>
                  <Card sx={{
                    background: 'linear-gradient(135deg, #4caf5020 0%, #2e7d3220 100%)',
                    border: '2px solid #4caf5030',
                    borderRadius: 1,
                    overflow: 'hidden',
                    height: '100%',
                    minHeight: 150
                  }}>
                    <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1" sx={{
                        color: '#4caf50',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1,
                      }}>
                        <FeatherIcon icon="truck" size={14} />
                        Delivery Comments
                      </Typography>

                      <Box sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        maxHeight: 100,
                        '&::-webkit-scrollbar': {
                          width: '5px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#4caf50',
                          borderRadius: '8px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: '#45a049',
                        },
                      }}>
                        {row.delivery?.comments ? (
                          <Typography variant="body2" sx={{
                            color: '#4a5568',
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {row.delivery.comments}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{
                            color: '#a0aec0',
                            fontStyle: 'italic',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}>
                            <FeatherIcon icon="message-square" size={14} />
                            No delivery comments available
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2, opacity: 0.3 }} />

            {/* Enhanced Ordered Items Section with Overflow */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: '#2d3748',
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <FeatherIcon icon="list" size={18} />
                  Ordered Items
                  <Badge
                    badgeContent={row.orderedItems?.length || 0}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#667eea',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                </Typography>

                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: '#16a085',
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 5,
                  gap: 0.5,
                }}>
                  <FeatherIcon icon="dollar-sign" size={16} />
                  Total: {formatCurrency(totalOrderValue)}
                </Typography>
              </Stack>

              {row.orderedItems && row.orderedItems.length > 0 ? (
                <Box sx={{
                  maxHeight: '350px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  },
                }}>
                  <Grid container spacing={2}>
                    {row.orderedItems.map((item, index) => (
                      <Grid item xs={12} key={item.item_id || index}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                          border: '1px solid #e2e8f0',
                          borderRadius: 2,
                          overflow: 'hidden',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            boxShadow: '0 15px 30px rgba(0,0,0,0.08)',
                            transform: 'translateY(-6px)',
                            borderColor: '#667eea'
                          },
                        }}>
                          <CardContent sx={{ p: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                              {/* Enhanced Image Section */}
                              <Box sx={{
                                width: { xs: '100%', sm: 250 },
                                height: { xs: 200, sm: 250 },
                                position: 'relative',
                                overflow: 'hidden',
                              }}>
                                {item.images && item.images.length > 0 ? (
                                  <Box sx={{
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      background: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
                                      pointerEvents: 'none'
                                    }
                                  }}>
                                    <img
                                      src={item.images[0].signedUrl || item.images[0].url}
                                      alt={item.itemName || 'Item'}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transition: 'transform 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                    />
                                  </Box>
                                ) : (
                                  <Box sx={{
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 0.5
                                  }}>
                                    <Box sx={{
                                      p: 1.5,
                                      backgroundColor: '#667eea',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <FeatherIcon icon="package" size={28} color="white" />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                      No Image
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* Enhanced Item Details Section */}
                              <Box sx={{
                                flexGrow: 1,
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                              }}>
                                {/* Item Header */}
                                <Box>
                                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1}>
                                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                                      <Typography variant="h6" sx={{
                                        fontWeight: 700,
                                        fontSize: 16,
                                        color: '#1a202c',
                                        mb: 0.5,
                                        wordBreak: 'break-word'
                                      }}>
                                        {item.itemName || item.make || 'Unknown Item'}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{
                                        mb: 0.5,
                                        fontSize: '0.75rem',
                                        wordBreak: 'break-word'
                                      }}>
                                        Model: {item.modelNo || 'N/A'} | Make: {item.make || 'N/A'}
                                      </Typography>
                                    </Box>

                                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                      <Tooltip title="SKU" arrow>
                                        <Chip
                                          label={item.sku || 'N/A'}
                                          size="small"
                                          sx={{
                                            backgroundColor: '#e3f2fd',
                                            color: '#1976d2',
                                            fontWeight: 600,
                                            fontSize: '0.7rem'
                                          }}
                                        />
                                      </Tooltip>
                                      <Tooltip title="Category" arrow>
                                        <Chip
                                          label={item.itemCategory || 'N/A'}
                                          size="small"
                                          sx={{
                                            backgroundColor: '#f3e5f5',
                                            color: '#7b1fa2',
                                            fontWeight: 600,
                                            fontSize: '0.7rem'
                                          }}
                                        />
                                      </Tooltip>
                                    </Stack>
                                  </Stack>

                                  {/* Item Description with Overflow */}
                                  {item.description && item.description !== 'N/A' && (
                                    <Box sx={{
                                      mb: 1,
                                      p: 1,
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: 1,
                                      border: '1px solid #e9ecef',
                                      maxHeight: 60,
                                      overflow: 'auto',
                                      '&::-webkit-scrollbar': {
                                        width: '3px',
                                      },
                                      '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '8px',
                                      },
                                      '&::-webkit-scrollbar-thumb': {
                                        background: '#cbd5e0',
                                        borderRadius: '8px',
                                      }
                                    }}>
                                      <Typography variant="body2" color="text.secondary" sx={{
                                        fontSize: '0.75rem',
                                        lineHeight: 1.4,
                                        wordBreak: 'break-word',
                                        whiteSpace: 'pre-wrap'
                                      }}>
                                        {item.description}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                {/* Enhanced Metrics Grid */}
                                <Grid container spacing={1} sx={{ mt: 'auto' }}>
                                  {[
                                    {
                                      label: 'Requested Qty',
                                      value: item.requestedQuantity || 0,
                                      icon: 'hash',
                                      color: '#f57c00',
                                      bgColor: '#fff3e0',
                                      borderColor: '#ffcc02'
                                    },
                                    {
                                      label: 'Price/Unit',
                                      value: formatCurrency(item.itemCostPerUnit || item.price),
                                      icon: 'dollar-sign',
                                      color: '#2e7d32',
                                      bgColor: '#e8f5e9',
                                      borderColor: '#4caf50'
                                    },
                                    {
                                      label: 'Total Value',
                                      value: formatCurrency((item.requestedQuantity || 0) * (item.itemCostPerUnit || item.price || 0)),
                                      icon: 'shopping-cart',
                                      color: '#1976d2',
                                      bgColor: '#e3f2fd',
                                      borderColor: '#2196f3'
                                    },
                                    {
                                      label: 'Job Name',
                                      value: item.jobName || 'Not specified',
                                      icon: 'briefcase',
                                      color: '#c2185b',
                                      bgColor: '#fce4ec',
                                      borderColor: '#e91e63'
                                    }
                                  ].map((metric, metricIndex) => (
                                    <Grid item xs={6} sm={3} key={metricIndex}>
                                      <Card sx={{
                                        p: 1.5,
                                        backgroundColor: metric.bgColor,
                                        border: `2px solid ${metric.borderColor}`,
                                        borderRadius: 1,
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          transform: 'translateY(-1px)',
                                          boxShadow: `0 6px 20px ${metric.color}20`
                                        }
                                      }}>
                                        <Stack alignItems="center" spacing={0.5}>
                                          <Box sx={{
                                            p: 0.5,
                                            backgroundColor: metric.color,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}>
                                            <FeatherIcon icon={metric.icon} size={14} color="white" />
                                          </Box>
                                          <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{
                                              fontWeight: 500,
                                              fontSize: '0.7rem'
                                            }}>
                                              {metric.label}
                                            </Typography>
                                            <Typography variant="body2" sx={{
                                              fontWeight: 700,
                                              color: metric.color,
                                              fontSize: metric.label === 'Job Name' ? '0.8rem' : '0.9rem',
                                              wordBreak: 'break-word',
                                              lineHeight: 1.2
                                            }}>
                                              {metric.value}
                                            </Typography>
                                          </Box>
                                        </Stack>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : (
                <Card sx={{
                  border: '2px dashed #cbd5e0',
                  borderRadius: 2,
                  backgroundColor: '#f7fafc',
                  textAlign: 'center',
                  p: 3,
                  background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)'
                }}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: '#e2e8f0',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}>
                    <FeatherIcon icon="inbox" size={40} color="#a0aec0" />
                  </Box>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, fontSize: '1rem' }}>
                    No Items Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    This order doesn't have any items associated with it.
                  </Typography>
                </Card>
              )}
            </Box>

            <Divider sx={{ my: 2, opacity: 0.3 }} />

            {/* Enhanced Status Footer */}
            <Box sx={{
              mt: 3,
              p: 2,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.8) 100%)',
              borderRadius: 1,
              border: '1px solid #e2e8f0',
              backdropFilter: 'blur(8px)'
            }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{
                      p: 0.5,
                      backgroundColor: row.status === 'COMPLETED' ? '#4caf50' :
                        row.status === 'PENDING' ? '#ff9800' : '#6c757d',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FeatherIcon
                        icon={row.status === 'COMPLETED' ? 'check-circle' :
                          row.status === 'PENDING' ? 'clock' : 'circle'}
                        size={14}
                        color="white"
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        Order Status
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.875rem' }}>
                        {row.status || 'DRAFT'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{
                      p: 0.5,
                      backgroundColor: row.delivery?.status === 'DELIVERED' ? '#4caf50' :
                        row.delivery?.status === 'IN_TRANSIT' ? '#2196f3' : '#ff9800',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FeatherIcon
                        icon={row.delivery?.status === 'DELIVERED' ? 'check-circle' :
                          row.delivery?.status === 'IN_TRANSIT' ? 'truck' : 'clock'}
                        size={14}
                        color="white"
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        Delivery Status
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.875rem' }}>
                        {row.delivery?.status || 'PENDING'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{
                      p: 0.5,
                      backgroundColor: '#667eea',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FeatherIcon icon="calendar" size={14} color="white" />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        Last Updated
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.875rem' }}>
                        {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      );
    },
    showExpandColumn: true,
    expandByColumnOnly: false,
    expandHeaderColumnRenderer: ({ isAnyExpands }) => (
      <Tooltip title={isAnyExpands ? "Collapse All" : "Expand All"} arrow>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 1,
          '&:hover': {
            backgroundColor: '#f5f5f5'
          }
        }}>
          <FeatherIcon
            icon={isAnyExpands ? "minus-circle" : "plus-circle"}
            size={16}
            color="#667eea"
          />
        </Box>
      </Tooltip>
    ),
    expandColumnRenderer: ({ expanded }) => (
      <Tooltip title={expanded ? "Collapse" : "Expand Details"} arrow>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: '#f0f4ff',
            transform: 'scale(1.1)'
          }
        }}>
          <FeatherIcon
            icon={expanded ? "chevron-up" : "chevron-down"}
            size={14}
            color="#667eea"
          />
        </Box>
      </Tooltip>
    ),
  };

  const tableStyles = {
    '& .react-bootstrap-table': {
      borderRadius: 3,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    },
    '& .react-bootstrap-table table': {
      marginBottom: 0,
    },
    '& .react-bootstrap-table table thead th': {
      backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderBottom: 'none',
      fontWeight: 700,
      fontSize: '0.875rem',
      padding: '16px 12px',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
      zIndex: 10,
      textAlign: 'center',
      letterSpacing: '0.5px'
    },
    '& .react-bootstrap-table table tbody td': {
      padding: '16px 12px',
      borderBottom: '1px solid #e2e8f0',
      verticalAlign: 'middle',
      backgroundColor: 'rgba(255,255,255,0.9)',
      transition: 'all 0.2s ease'
    },
    '& .react-bootstrap-table table tbody tr:hover': {
      backgroundColor: 'rgba(102, 126, 234, 0.04)',
      transform: 'translateX(2px)',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
    },
    '& .react-bootstrap-table table tbody tr.expanded': {
      backgroundColor: 'rgba(102, 126, 234, 0.08)',
    },
    '& .pagination': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      borderTop: '1px solid #e2e8f0',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    },
    '& .page-link': {
      border: '2px solid #667eea',
      color: '#667eea',
      padding: '10px 15px',
      marginLeft: '4px',
      marginRight: '4px',
      lineHeight: 1.25,
      backgroundColor: '#fff',
      borderRadius: '8px',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#667eea',
        borderColor: '#667eea',
        color: '#fff',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
      }
    },
    '& .page-item.active .page-link': {
      backgroundColor: '#667eea',
      borderColor: '#667eea',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
    },
    '& .page-item:first-child .page-link': {
      borderRadius: '8px',
    },
    '& .page-item:last-child .page-link': {
      borderRadius: '8px',
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        border: '1px solid #e2e8f0',
        ...tableStyles,
        '& thead th:nth-of-type(1)': {
          width: '50px !important',
        },
        '& thead th:nth-of-type(2)': {
          width: '120px !important',
        },
        '& thead th:nth-of-type(3)': {
          width: '200px !important',
        },
        '& thead th:nth-of-type(4)': {
          width: '150px !important',
        },
        '& thead th:nth-of-type(5)': {
          width: '100px !important',
        },
        ...(isRMVendor ? {
          '& thead th:nth-of-type(6)': {
            width: '140px !important',
          },
          '& thead th:nth-of-type(7)': {
            width: '150px !important',
          }
        } : {
          '& thead th:nth-of-type(6)': {
            width: '120px !important',
          },
          '& thead th:nth-of-type(7)': {
            width: '140px !important',
          },
          '& thead th:nth-of-type(8)': {
            width: '150px !important',
          }
        })
      }}
      className="miui-table scrollable-table"
    >
      <Box sx={{
        minHeight: "80vh",
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '12px',
          height: '12px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
        },
        '&::-webkit-scrollbar-corner': {
          background: '#f1f1f1'
        }
      }}>
        <BootstrapTable
          keyField="_id"
          data={orders || []}
          columns={columns}
          pagination={paginationFactory(paginationOptions)}
          bootstrap4
          bordered={false}
          hover
          responsive
          noDataIndication={NoDataIndication}
          wrapperClasses="table-responsive"
          expandRow={expandRow}
          rowStyle={{ cursor: 'pointer' }}
        />
      </Box>
    </Paper>
  );
};

export { InventoryOrdersTable };